import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Alert } from "@/atoms/alert";
import { Button } from "@/atoms/button";
import { EmptyState } from "@/components/empty-state";
import { PayPage } from "@/components/pay-page";
import { useCoachColour } from "@/design/use-coach-colour";
import { PaymentSettled } from "@/components/payment-settled";
import { CheckoutSkeleton } from "./checkout-skeleton";
import { CoachHeader } from "./coach-header";
import { PaymentSheet } from "./payment-sheet";
import { ProductBlock } from "./product-block";
import { Receipt } from "./receipt";
import { trackPaymentLinkVisit, useCheckoutDetails } from "@/data/checkout";
import {
  fontSource,
  stripeAppearance,
  stripeLocale,
} from "@/stripe/appearance";
import {
  detectPreferredCurrency,
  resolveCheckoutCurrency,
} from "@/utils/currency";
import { coachLabel, sellerOf, sentence } from "@/utils/coach";
import { getTrackingMetadata } from "@/utils/tracking";
import type { AppliedPromo } from "./coupon-field";
import type { PaymentOutcome } from "./use-checkout-payment";
import type {
  PublicCheckoutProduct,
  PublicCheckoutProductPrice,
} from "@/data/checkout";
import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";

/**
 * The page a payment link opens (template checkout 1.4; sheet DS28).
 *
 * One sheet, one reading: the coach, then the product with the amount as the largest number on
 * the page, then the wallet, the card, the total, the consent and one orange button. The two-step
 * form is gone; Stripe Elements is mounted in deferred mode, so nothing is created on the server
 * until the person actually pays.
 *
 * The coach's colour takes orange's ONE JOB here and nothing more (rules 144, 145; Mo,
 * 8 September 2026). The page stays paper, the surfaces stay hairlines, the statuses stay
 * semantic; what changes hands is the primary, its states, the focus line, the tints and
 * orange-as-text. `secondaryColor` is read by nothing: that was the old page wash.
 */

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "";

type Done = { outcome: PaymentOutcome } | null;

export function CheckoutPage() {
  const { t, i18n } = useTranslation();
  const { paymentLinkId } = useParams<{ paymentLinkId: string }>();
  const { data, isLoading, isError, error, refetch, isRefetching } =
    useCheckoutDetails(paymentLinkId);

  /* The coach's colour takes orange's one job as soon as the payload lands, and gives it back on
     unmount. Applied in a layout effect so Stripe's appearance reads the coach's tokens and not
     Gymkee's on the first mount. */
  const coachHex = useCoachColour(data?.business.brandColor);

  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeBlocked, setStripeBlocked] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [currency, setCurrency] = useState<string | null>(null);
  const [promo, setPromo] = useState<AppliedPromo | null>(null);
  const [done, setDone] = useState<Done>(null);
  const [returnRefused, setReturnRefused] = useState(false);

  const availableCurrencies = useMemo(
    () => data?.availableCurrencies ?? [],
    [data],
  );
  const defaultCurrency =
    data?.defaultCurrency ?? data?.products[0]?.price?.currency ?? "eur";

  useEffect(() => {
    if (!data || currency) return;
    setCurrency(
      resolveCheckoutCurrency(
        availableCurrencies,
        defaultCurrency,
        detectPreferredCurrency(),
      ),
    );
  }, [data, currency, availableCurrencies, defaultCurrency]);

  useEffect(() => {
    if (!paymentLinkId || !data) return;
    void getTrackingMetadata().then((metadata) =>
      trackPaymentLinkVisit(paymentLinkId, metadata),
    );
  }, [paymentLinkId, data]);

  /*
   * Stripe, and the 3DS return.
   *
   * A card that needed the bank comes back to this URL with the intent's client secret in the
   * query. Three answers matter and all three are now drawn: succeeded and processing show the
   * receipt, and `requires_payment_method` or `canceled` say the bank refused and nothing was
   * charged. The old page silently re-rendered an empty form for the third, which is the
   * twelfth flaw this lane was opened on.
   */
  useEffect(() => {
    if (!data) return;
    let cancelled = false;

    void (async () => {
      try {
        const instance = await loadStripe(STRIPE_KEY, {
          stripeAccount: data.stripeAccountId,
        });
        if (cancelled) return;
        if (!instance) {
          setStripeBlocked(true);
          setStripeLoading(false);
          return;
        }
        setStripe(instance);

        const params = new URLSearchParams(window.location.search);
        const clientSecret = params.get("payment_intent_client_secret");
        if (clientSecret) {
          const { paymentIntent } =
            await instance.retrievePaymentIntent(clientSecret);
          if (cancelled) return;
          if (
            paymentIntent?.status === "succeeded" ||
            paymentIntent?.status === "processing"
          ) {
            setDone({
              outcome: {
                kind: paymentIntent.status,
                amountCents: paymentIntent.amount,
                email: paymentIntent.receipt_email ?? "",
              },
            });
          } else if (
            paymentIntent?.status === "requires_payment_method" ||
            paymentIntent?.status === "canceled"
          ) {
            setReturnRefused(true);
          }
          window.history.replaceState({}, "", window.location.pathname);
        }
      } catch {
        if (!cancelled) setStripeBlocked(true);
      } finally {
        if (!cancelled) setStripeLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const priceFor = useCallback(
    (product: PublicCheckoutProduct): PublicCheckoutProductPrice => {
      const target = currency ?? defaultCurrency;
      const match = product.prices?.find(
        (price) => price.currency.toLowerCase() === target.toLowerCase(),
      );
      return match ?? product.prices?.[0] ?? product.price;
    },
    [currency, defaultCurrency],
  );

  const changeCurrency = useCallback((next: string) => {
    setCurrency(next);
    setPromo(null);
    try {
      sessionStorage.setItem("gymkee_checkout_currency", next);
    } catch {
      // Private browsing refuses sessionStorage; the choice simply does not survive a 3DS hop.
    }
  }, []);

  const initialPromoCode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("promo") ?? params.get("code") ?? params.get("coupon") ?? ""
    );
  }, []);

  /* The seller for the header, which is drawn only when the coach has a name, and the name for
     every sentence, which always has words in it. */
  const seller = sellerOf(data?.business);
  const coachName = coachLabel(seller?.name, t);
  const supportEmail = data?.business.supportEmail;
  const language = i18n.resolvedLanguage ?? i18n.language;

  if (isLoading || !paymentLinkId || (data && (stripeLoading || !currency))) {
    return (
      <PayPage>
        <main>
          <CheckoutSkeleton />
        </main>
      </PayPage>
    );
  }

  if (isError || !data) {
    const status = (error as { response?: { status?: number } } | null)
      ?.response?.status;
    const closed = status === 404 || status === 400;
    return (
      <PayPage>
        <main>
          <EmptyState
            heading={
              closed
                ? t("gymkeePay.checkout.linkClosedHeading")
                : t("gymkeePay.checkout.loadFailedHeading")
            }
            line={
              closed
                ? sentence(
                    t("gymkeePay.checkout.linkClosedLine", { coach: coachName }),
                  )
                : undefined
            }
            action={
              closed ? (
                supportEmail ? (
                  <Button variant="tertiary" href={`mailto:${supportEmail}`}>
                    {t("gymkeePay.common.messageCoach", { coach: coachName })}
                  </Button>
                ) : undefined
              ) : (
                <Button
                  variant="secondary"
                  loading={isRefetching}
                  onClick={() => void refetch()}
                >
                  {t("gymkeePay.common.tryAgain")}
                </Button>
              )
            }
          />
        </main>
      </PayPage>
    );
  }

  const products = data.products;
  const mainProduct = products[0];
  const mainPrice = priceFor(mainProduct);
  const isSubscription = data.mode === "subscription";
  const grossCents = products.reduce(
    (sum, product) => sum + priceFor(product).unitAmount,
    0,
  );
  const totalCents = promo ? promo.discountedAmountCents : grossCents;
  const activeCurrency = currency ?? defaultCurrency;

  const elementsOptions: StripeElementsOptions = {
    mode: isSubscription ? "subscription" : "payment",
    amount: Math.max(1, totalCents),
    currency: activeCurrency,
    setupFutureUsage: isSubscription ? "off_session" : undefined,
    /* One method, so Stripe draws the fields and nothing around them: no tab bar, no accordion
       header, no card inside our card. Apple Pay and Google Pay are card-family and still reach
       the Express Checkout Element above. */
    paymentMethodTypes: ["card"],
    appearance: stripeAppearance(),
    fonts: [fontSource()],
    locale: stripeLocale(language),
  };

  return (
    <PayPage>
      <main className="gap-gk-6 flex flex-col">
        {seller ? <CoachHeader seller={seller} /> : null}
        {done ? (
          <PaymentSettled>
            <Receipt
              status={done.outcome.kind}
              coachName={coachName}
              email={done.outcome.email}
              paidCents={done.outcome.amountCents}
              currency={activeCurrency}
              product={mainProduct}
              price={mainPrice}
              isSubscription={isSubscription}
            />
          </PaymentSettled>
        ) : (
          <>
            <ProductBlock
              product={mainProduct}
              price={mainPrice}
              isSubscription={isSubscription}
              totalCents={totalCents}
              previousTotalCents={promo ? promo.originalAmountCents : null}
              currency={activeCurrency}
              availableCurrencies={availableCurrencies}
              onCurrencyChange={changeCurrency}
              coachName={coachName}
            />

            {returnRefused && (
              <Alert tone="danger">{t("gymkeePay.checkout.bankRefused")}</Alert>
            )}

            {stripeBlocked || !stripe ? (
              <Alert
                tone="warning"
                action={
                  <Button
                    variant="tertiary"
                    onClick={() => window.location.reload()}
                  >
                    {t("gymkeePay.common.reload")}
                  </Button>
                }
              >
                {t("gymkeePay.common.stripeBlocked")}
              </Alert>
            ) : (
              /* The currency remounts the whole Elements tree, because a different currency is a
                 different payment, and so does the coach's colour, because Stripe reads our
                 tokens once when the provider mounts. A changed total does not: a card already
                 typed must survive a discount code (the amount is pushed with elements.update). */
              <Elements
                key={`${activeCurrency}-${coachHex ?? "gymkee"}`}
                stripe={stripe}
                options={elementsOptions}
              >
                <PaymentSheet
                  checkout={data}
                  products={products}
                  priceFor={priceFor}
                  mainPrice={mainPrice}
                  isSubscription={isSubscription}
                  currency={activeCurrency}
                  totalCents={totalCents}
                  promo={promo}
                  onPromoChange={setPromo}
                  initialPromoCode={initialPromoCode}
                  coachName={coachName}
                  onDone={(outcome) => setDone({ outcome })}
                />
              </Elements>
            )}
          </>
        )}
      </main>
    </PayPage>
  );
}
